import React from 'react';
import styles from './index.module.sass';
import iconParkingCar from 'assets/icon_parking.svg';
import iconNavigate from 'assets/icon_navigate.svg';
import iconHand from 'assets/icon_hand.svg';
import Card from 'components/card';
import Carousel from 'components/carousel';
import ButtonDialog from 'components/button-dialog';
import amplitude from 'service/amplitude';
import { amountFormat } from 'helpers/currency-helper';
import { durationTime } from 'helpers/time-helper';
import {
  ParkingRecordProvider,
  useParkingRecordContext,
  SET_CURRENT_SELECT_PLATE_NUMBER,
  SET_PARKING_RECORD_LEAVE_DIALOG,
  SET_PARKING_RECORD_CANCEL_DIALOG,
  SET_PARKING_RECORD_DIALOG,
} from './use-parking-reocrd';
import { PATH } from 'config';
import { useAppContext } from 'use-app';
import LoadingDialog from 'components/loading-dialog';
import { useHistory, useLocation } from 'react-router-dom';
import { getUserInfo } from 'helpers/data-check-helper';
import { isCpcpay } from 'config';
import { isNpcpay } from 'config';

const Navigation = ({ record }) => {
  return (
    <a
      href={`https://www.google.com/maps/dir/?api=1&destination=${record.parkinglot.lat},${record.parkinglot.lng}`}
      rel="noopener noreferrer"
      target="_blank"
      onClick={() => amplitude.main_parking_navigation_onClick()}
    >
      <img src={iconNavigate} alt="navigate" />
    </a>
  );
};

const ParkingRecord = ({ record }) => {
  const { dispatch } = useParkingRecordContext();

  const parkingRecordDialogOpen = plateNumber => {
    dispatch({
      type: SET_PARKING_RECORD_DIALOG,
      payload: true,
    });
    dispatch({
      type: SET_CURRENT_SELECT_PLATE_NUMBER,
      payload: plateNumber,
    });
  };

  const paringRecordSubtitle = record => {
    if (record.parkinglot) {
      return record.parkinglotName;
    } else {
      return '歡迎使用麻吉付！';
    }
  };

  return (
    <Card>
      <div className={styles.card}>
        <div
          className={styles.help}
          onClick={() => {
            amplitude.main_parking_help_onClick();
            parkingRecordDialogOpen(record.plateNumber);
          }}
        >
          <img src={iconHand} alt="hand" />
          幫助
        </div>
        <div className={styles.title}>{record.plateNumber}</div>
        <div className={styles.title}>
          {paringRecordSubtitle(record)}
          {record.parkinglot && <Navigation record={record} />}
        </div>
        <div className={styles.list}>
          <div className={styles.item}>
            <span>停車時數</span>
            <span>{durationTime(record.parkingDate)}</span>
          </div>
          <div className={styles.item}>
            <span>預估金額</span>
            <span>${amountFormat(record.parkingAmount)}</span>
          </div>
          <div className={styles.item}>
            <span>優惠折抵</span>
            <span>-${amountFormat(record.parkingDiscount)}</span>
          </div>
        </div>
      </div>
    </Card>
  );
};

const ParkingRecords = () => {
  const { state: appState } = useAppContext();
  const parkingRecordLength = appState.parkingRecord.length;
  const { hasPayments } = getUserInfo(appState);
  const { state, dispatch, setParkingRecordLeave, setParkingRecordCancel } = useParkingRecordContext();
  const history = useHistory();
  const location = useLocation();

  if (parkingRecordLength === 0) return <></>;

  const parkingRecordDialogHide = () => {
    dispatch({
      type: SET_PARKING_RECORD_DIALOG,
      payload: false,
    });
  };

  const setParkingRecordLeaveDialog = payload => {
    dispatch({
      type: SET_PARKING_RECORD_LEAVE_DIALOG,
      payload,
    });
  };

  const setParkingRecordCancelDialog = payload => {
    dispatch({
      type: SET_PARKING_RECORD_CANCEL_DIALOG,
      payload,
    });
  };

  const alreadyPaid = () => {
    amplitude.main_parking_help_paid_onClick();
    setParkingRecordLeaveDialog(true);
    parkingRecordDialogHide();
  };

  const alreadyPaidConfirm = () => {
    setParkingRecordLeave();
  };

  const alreadyPaidCancel = () => {
    setParkingRecordLeaveDialog(false);
  };

  const notParking = () => {
    amplitude.main_parking_help_notParking_onClick();
    setParkingRecordCancelDialog(true);
    parkingRecordDialogHide();
  };

  const notParkingConfirm = () => {
    setParkingRecordCancel();
  };

  const notParkingCancel = () => {
    setParkingRecordCancelDialog(false);
  };

  const openContactForm = () => {
    amplitude.main_parking_help_more_onClick();
    const record = appState.parkingRecord.find(x => x.plateNumber === state.currentSelectPlateNumber) || {};
    history.push({
      pathname: PATH.CONTACT,
      state: {
        from: location.pathname,
        poi: record.parkinglotName === '歡迎使用麻吉付！' ? '' : record.parkinglotName,
        plateNumber: record.plateNumber,
        issueGroupKey: 'Onsite',
      },
    });
  };

  const closeDialog = () => {
    amplitude.main_parking_help_cancel_onClick();
    parkingRecordDialogHide();
  };

  const PaymentHint = () => {
    if (hasPayments) return <></>;
    return (
      <div className={styles['payment-hint']}>
        完成付款設定，出場時自動扣款免去繳費機。
        <span
          onClick={() => {
            if (isCpcpay) {
              history.push(PATH.MOCHIPAY_FLOW_CONFIRM);
            } else if (isNpcpay) {
              history.push(PATH.MOCHIPAY_FLOW_SETTING);
            } else {
              history.push({
                pathname: PATH.MOCHIPAY_FLOW_CREDIT_CARD,
                state: { originPath: 'main' },
              });
            }
          }}
        >
          前往設定
        </span>
      </div>
    );
  };

  const CancelingText = () => {
    let { isPayTaipei } = appState.parkingRecord.find(x => x.plateNumber === state.currentSelectPlateNumber) || {};

    return isPayTaipei
      ? '公有路外停車場（含委外）因現場系統限制，若出場仍發生車牌辨識錯誤狀況，將會造成誤扣款問題。我們收到您的回報後，客服將儘速為您申訴退款，感謝您的配合。'
      : '可能因現場設備辨識錯誤等問題導致錯誤通知，您可關閉停車紀錄，並不會透過“麻吉付“收取此筆停車費用。';
  };

  return (
    <div className={styles['parking-records']}>
      <LoadingDialog isShow={state.loading} />
      <ButtonDialog
        isShow={state.parkingRecordDialog}
        title={'請問您需要什麼幫助呢？'}
        items={[
          {
            content: '我已繳費離場',
            theme: 'positive',
            onClick: alreadyPaid,
          },
          {
            content: '我沒有去停車',
            theme: 'positive',
            onClick: notParking,
          },
          {
            content: '進一步協助',
            theme: 'positive',
            onClick: openContactForm,
          },
          {
            content: '取消',
            theme: 'cancel',
            onClick: closeDialog,
          },
        ]}
      />
      <ButtonDialog
        isShow={state.parkingRecordCancelDialog}
        title={`${state.currentSelectPlateNumber} 沒有去停車`}
        horizonal
        items={[
          {
            content: '確定關閉',
            theme: 'positive',
            onClick: notParkingConfirm,
          },
          {
            content: '取消',
            theme: 'cancel',
            onClick: notParkingCancel,
          },
        ]}
      >
        <CancelingText />
      </ButtonDialog>

      <ButtonDialog
        isShow={state.parkingRecordLeaveDialog}
        title={`${state.currentSelectPlateNumber} 已離場？`}
        horizonal
        items={[
          {
            content: '確定關閉',
            theme: 'positive',
            onClick: alreadyPaidConfirm,
          },
          {
            content: '取消',
            theme: 'cancel',
            onClick: alreadyPaidCancel,
          },
        ]}
      >
        若您已確定離場，我們將關閉此停車紀錄，並不會透過“麻吉付“收取此筆停車費用。
      </ButtonDialog>
      <h3>
        <img src={iconParkingCar} alt="parking-cars" />
        {parkingRecordLength} 台車正在停車中
      </h3>
      <PaymentHint />
      <Carousel
        loading={false}
        items={appState.parkingRecord.map(record => {
          return <ParkingRecord key={record.id} record={record} />;
        })}
        fullWidth={true}
      />
    </div>
  );
};

const ParkingRecordsContainer = () => {
  return (
    <ParkingRecordProvider>
      <ParkingRecords />
    </ParkingRecordProvider>
  );
};

export default ParkingRecordsContainer;
